import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ButtonUI,
  type ButtonUIProps,
  Checkbox,
  InputSearch,
  Selector,
  type SelectorOptionPropsInterface,
  TaptapList
} from 'sonata-react-components';
import { DigitalAdExchangeCatalog, DOOHAdExchangeCatalog } from 'sonata-static-storage/lib/samples/adExchanges.samples';
import { AdExchangeCodesEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/adExchange.schema';
import { DealPublisherInterface } from 'sonata-static-storage/lib/schemas/premium/dealPublisher.schema';

import { getDateSeed } from '@/helpers/common/seed.helper';
import { countriesISO2Options } from '@/helpers/countriesOptions/countriesOptions.helper';
import { getFilteredDeals, updateDeal as updateDealService } from '@/services/deals.service';
import { getDealRow } from './helpers';

import type { DealInterface } from 'sonata-static-storage/lib/schemas/premium/deal.schema';
import type { DealsFilters } from '@/services/deals.service';

type TaptapListItem = Pick<
  DealInterface,
  '_id' | 'active' | 'alias' | 'bidFloor' | 'origin' | 'type' | 'seller' | 'country' | 'sourceType'
> & {
  bidFloorText: string;
  editButton: ButtonUIProps;
  id: string;
  publisher: string;
};

type Filters = {
  active?: boolean;
  adExchange?: DealInterface['origin']['name'];
  country?: DealInterface['country'];
  search?: string;
};

type Props = {
  dealsPublishers?: DealPublisherInterface<string>[];
  editedDeal?: Partial<DealInterface<string>>;
  onlyUserDeals?: boolean;
  setEditedDeal: (deal?: Partial<DealInterface<string>>) => void;
};

type State = {
  filters?: Filters;
  seed: string;
};

export function DealsTable({
  dealsPublishers = [],
  editedDeal,
  onlyUserDeals = false,
  setEditedDeal
}: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [{ filters, seed }, setState] = useState<State>({
    filters: {},
    seed: getDateSeed()
  });

  const dealsToken: Record<string, any> = {};

  const BASE_ID = 'dealsManager';

  useEffect(() => {
    return () => {
      dealsToken?.cancel?.();
    };
  }, []);

  const fetchDeals = useCallback(
    async ({ page, pageSize }: { page: number; pageSize: number }) => {
      const payload: DealsFilters = {
        active: filters?.active,
        adExchange: filters?.adExchange,
        country: filters?.country,
        itemsPerPage: pageSize.toString(),
        page,
        search: filters?.search
      };
      const dealsResult = await getFilteredDeals(dealsToken, payload, onlyUserDeals);
      const data = dealsResult.items.map(getDealRow(formatMessage, setEditedDeal, dealsPublishers));
      return {
        data,
        page: dealsResult.currentPage ?? page,
        pageSize: dealsResult.itemsPerPage ?? pageSize,
        total: dealsResult.totalItems ?? dealsResult.items.length,
        totalPages: Math.max(
          1,
          Math.ceil((dealsResult.totalItems ?? dealsResult.items.length) / (dealsResult.itemsPerPage ?? pageSize))
        )
      };
    },
    [seed, editedDeal, filters]
  );

  const handleCountryChange = (countryOption: SelectorOptionPropsInterface): void => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        country: countryOption.id as string
      },
      seed: getDateSeed()
    }));
  };

  const adExchangeCatalog = {
    ...DigitalAdExchangeCatalog,
    ...DOOHAdExchangeCatalog
  };

  const handleSelectAdExchange = (option: SelectorOptionPropsInterface): void => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        adExchange: option?.id ? AdExchangeCodesEnum[option.id as keyof typeof AdExchangeCodesEnum] : undefined
      },
      seed: getDateSeed()
    }));
  };

  const handleStatusChange = (statusOption: SelectorOptionPropsInterface | null): void => {
    let activeValue: boolean | undefined;
    if (statusOption && statusOption.id === 'true') {
      activeValue = true;
    } else if (statusOption && statusOption.id === 'false') {
      activeValue = false;
    }
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        active: activeValue
      },
      seed: getDateSeed()
    }));
  };

  const handleSearchChange = (search: string) => {
    setState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        search
      },
      seed: getDateSeed()
    }));
  };

  const handleSelectionChange = (selectedItems: ({ id: string } & any)[]) => {
    setState(prev => ({
      ...prev,
      selectedVenueIds: selectedItems.map(v => v.id)
    }));
  };

  const adExchangeOptions: SelectorOptionPropsInterface[] = Object.values(adExchangeCatalog).map(exchange => ({
    id: exchange.code,
    label: exchange.name
  }));

  const statusOptions: SelectorOptionPropsInterface[] = [
    {
      id: 'all',
      label: formatMessage({
        defaultMessage: 'All',
        id: 'common.all'
      })
    },
    {
      id: 'true',
      label: formatMessage({
        defaultMessage: 'Active',
        id: 'common.active'
      })
    },
    {
      id: 'false',
      label: formatMessage({
        defaultMessage: 'Inactive',
        id: 'common.inactive'
      })
    }
  ];

  const updateDealActive = (item: TaptapListItem) => async () => {
    const token = {};
    await updateDealService({ active: !item.active, bidFloor: item.bidFloor }, item._id!.toString(), token);
    setState(prev => ({
      ...prev,
      seed: getDateSeed()
    }));
  };

  return (
    <>
      <div className="dealsManager__filters">
        <Selector
          className="dealsManager__filtersStatus"
          id={`${BASE_ID}_statusSelector`}
          isClearable
          label={formatMessage({
            defaultMessage: 'Status',
            id: 'menu.dealsManager.filter.status'
          })}
          onChange={handleStatusChange}
          options={statusOptions}
          tiny
          type="simple"
        />
        <Selector
          className="dealsManager__filtersCountry"
          id={`${BASE_ID}_venueCountrySelector`}
          isClearable
          label={formatMessage({
            defaultMessage: 'Country',
            id: 'menu.dealsManager.filter.country'
          })}
          onChange={handleCountryChange}
          options={countriesISO2Options}
          showSearch
          tiny
          type="simple"
        />
        <Selector
          className="dealsManager__filtersAdExchange"
          id={`${BASE_ID}_adExchangeSelector`}
          isClearable
          label={formatMessage({
            defaultMessage: 'Ad Exchange',
            id: 'menu.dealsManager.filter.adExchange'
          })}
          onChange={handleSelectAdExchange}
          options={adExchangeOptions}
          showSearch
          tiny
          type="simple"
        />
        <InputSearch
          className="dealsManager__filtersText"
          id={`${BASE_ID}_searchByText`}
          lens
          name="dealsManagerSearch"
          onChange={handleSearchChange}
          placeholder={formatMessage({
            defaultMessage: 'Search by name, category...',
            id: 'menu.dealsManager.filter.textPlaceholder'
          })}
          tiny
        />
      </div>
      <div className="dealsManager__listContainer">
        <TaptapList
          className="dealsManager__list"
          fetchData={fetchDeals}
          id="dealsManager-list"
          key="dealsManagerList"
          onSelectionChange={handleSelectionChange}
        >
          <TaptapList.ColumnsBar>
            <TaptapList.ColumnsBar.Column
              className="active"
              description={formatMessage({ defaultMessage: 'Active', id: 'menu.dealsManager.list.column.active' })}
              id="active"
            />
            <TaptapList.ColumnsBar.Column
              className="alias"
              description={formatMessage({ defaultMessage: 'Name', id: 'menu.dealsManager.list.column.alias' })}
              id="alias"
            />
            <TaptapList.ColumnsBar.Column
              className="id"
              description={formatMessage({ defaultMessage: 'ID', id: 'menu.dealsManager.list.column.id' })}
              id="id"
            />
            <TaptapList.ColumnsBar.Column
              className="adExchange"
              description={formatMessage({
                defaultMessage: 'AdExchange',
                id: 'menu.dealsManager.list.column.adExchange'
              })}
              id="adExchange"
            />
            <TaptapList.ColumnsBar.Column
              className="type"
              description={formatMessage({ defaultMessage: 'Type', id: 'menu.dealsManager.list.column.type' })}
              id="type"
            />
            <TaptapList.ColumnsBar.Column
              className="seller"
              description={formatMessage({ defaultMessage: 'Seller', id: 'menu.dealsManager.list.column.seller' })}
              id="seller"
            />
            <TaptapList.ColumnsBar.Column
              className="publisher"
              description={formatMessage({
                defaultMessage: 'Publisher',
                id: 'menu.dealsManager.list.column.publisher'
              })}
              id="publisher"
            />
            <TaptapList.ColumnsBar.Column
              className="bidFloor"
              description={formatMessage({
                defaultMessage: 'BidFloor',
                id: 'menu.dealsManager.list.column.bidFloor'
              })}
              id="bidFloor"
            />
            <TaptapList.ColumnsBar.Column
              className="country"
              description={formatMessage({
                defaultMessage: 'Country',
                id: 'menu.dealsManager.list.column.country'
              })}
              id="country"
            />
            <TaptapList.ColumnsBar.Column
              className="source"
              description={formatMessage({ defaultMessage: 'Source', id: 'menu.dealsManager.list.column.source' })}
              id="source"
            />
          </TaptapList.ColumnsBar>
          <TaptapList.RowsListVirtualized<TaptapListItem>
            emptyStateDescription={formatMessage({
              defaultMessage: 'Try adjusting your search or filters to find venues.',
              id: 'menu.dealsManager.list.emptyState.description'
            })}
            emptyStateIcon="fal fa-empty-set"
            emptyStateTitle={formatMessage({
              defaultMessage: 'No results match your filters',
              id: 'menu.dealsManager.list.emptyState.title'
            })}
          >
            {items =>
              items.map(item => (
                <TaptapList.RowsListVirtualized.Row id={item.id} key={item.id} virtualData={item.virtualData}>
                  <div className="active">
                    <Checkbox
                      active={item.active}
                      classes="dealsManager__activeToggle"
                      id={`dealsManager_active_${item.id}`}
                      isSwitch
                      onClickAction={updateDealActive(item)}
                    />
                  </div>
                  <span className="alias" title={item.alias}>
                    {item.alias}
                  </span>
                  <span className="id" title={item.id}>
                    {item.id}
                  </span>
                  <span
                    className="adExchange"
                    title={Array.isArray(item.origin?.name) ? item.origin.name.join(', ') : item.origin?.name}
                  >
                    {Array.isArray(item.origin?.name) ? item.origin.name.join(', ') : item.origin?.name}
                  </span>
                  <span className="type" title={item.type}>
                    {item.type}
                  </span>
                  <span className="seller" title={item.seller}>
                    {item.seller}
                  </span>
                  <span className="publisher" title={item.publisher}>
                    {item.publisher}
                  </span>
                  <span className="bidFloor" title={item.bidFloorText}>
                    {item.bidFloorText}
                  </span>
                  <span className="country" title={item.country}>
                    {item.country}
                  </span>
                  <span className="source" title={`${item.sourceType}`}>
                    {item.sourceType}
                  </span>
                  <div className="dealsManager__listActions">
                    <ButtonUI
                      className="dealsManager__dealEditButton"
                      icon={item.editButton.icon}
                      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                        e.stopPropagation();
                        item.editButton.onClick?.();
                      }}
                      shape={item.editButton.shape}
                      size={item.editButton.size}
                      type={item.editButton.type}
                    />
                  </div>
                </TaptapList.RowsListVirtualized.Row>
              ))
            }
          </TaptapList.RowsListVirtualized>
        </TaptapList>
      </div>
    </>
  );
}
